import { supabase } from "@/lib/supabase/client";
import { getApiUrl } from "@/lib/api/utils";
import { isDefinitiveInvalidRefreshTokenError } from "@/lib/auth/refresh-token-errors";
import { isCapacitorApp, isAndroid, nativePlatformHeader } from "@/lib/utils";

export type AuthResponse = {
  success: boolean;
  message?: string;
  /** API error code e.g. "captcha_failed" for human verification errors */
  errorCode?: string;
  /** Raw error from API (e.g. Supabase message), only in development */
  details?: string;
  user?: {
    id: string;
    email: string;
  };
  session?: {
    access_token: string;
    refresh_token: string;
  };
  preauthToken?: string; // Preauth token for OTP verification
  /** True when signup was resumed (existing unconfirmed user continued verification) */
  resumed?: boolean;
  /** True when the account was created but Supabase requires email confirmation
   * before a session exists (no session returned). The signup page shows a
   * "check your inbox" step in this case. */
  needsEmailConfirmation?: boolean;
  /** Password was right, but this device has to pass the emailed code first (no session yet). */
  requiresStepUp?: boolean;
  requiresPhone?: boolean; // User needs to add phone number
  stepUpReason?: "new_device" | "failed_attempts" | "suspicious";
  /** Opaque token for /api/auth/login-code/*. Only present with requiresStepUp. */
  challengeToken?: string;
  /** e.g. "t***@example.com", so the member knows which inbox to check. */
  emailHint?: string;
  /** False when the email provider failed: the member can press "Send a new code" straight away. */
  codeSent?: boolean;
  resendInSeconds?: number;
};

export async function signUp(
  email: string,
  password: string,
  turnstileToken?: string,
  consent?: { agreeToTerms: boolean; marketingOptIn?: boolean; firstName?: string; lastName?: string; dateOfBirth?: string }
): Promise<AuthResponse> {
  const clientStartTime = Date.now();
  console.log("[signUp] 📤 Client request started", {
    timestamp: new Date().toISOString(),
    hasToken: !!turnstileToken,
  });
  
  try {
    // Enhanced email validation to catch fake/invalid emails
    const { validateEmail } = await import("@/lib/validation/email");
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      return {
        success: false,
        message: emailValidation.error || "Invalid email address. Please use a real email address.",
      };
    }

    // Call signup API route (which handles Turnstile verification and Supabase signup)
    const fetchStartTime = Date.now();
    const signupHeaders: Record<string, string> = { "Content-Type": "application/json" };
    const signupPlatform = nativePlatformHeader();
    if (signupPlatform) {
      signupHeaders["X-Platform"] = signupPlatform;
    }
    const response = await fetch(getApiUrl("/api/auth/signup"), {
      method: "POST",
      headers: signupHeaders,
      body: JSON.stringify({
        email: email.toLowerCase().trim(),
        password,
        turnstileToken,
        agreeToTerms: consent?.agreeToTerms ?? false,
        marketingOptIn: consent?.marketingOptIn ?? false,
        firstName: consent?.firstName?.trim(),
        lastName: consent?.lastName?.trim(),
        dateOfBirth: consent?.dateOfBirth,
      }),
    });

    const fetchTime = Date.now() - fetchStartTime;
    console.log("[signUp] 📥 Fetch complete", {
      ok: response.ok,
      fetchTime: `${fetchTime}ms`,
      timestamp: new Date().toISOString(),
    });

    const data = await response.json();
    
    const totalTime = Date.now() - clientStartTime;
    console.log("[signUp] ✅ Client request complete", {
      success: data.success,
      totalTime: `${totalTime}ms`,
      timestamp: new Date().toISOString(),
    });

    if (!response.ok) {
      return {
        success: false,
        message: data.error || "We couldn't create your account. Please try again.",
      };
    }

    if (!data.user) {
      return {
        success: false,
        message: "Account creation failed. Please try again.",
      };
    }

    // Set session if available (API route returns session data)
    if (data.session?.access_token && data.session?.refresh_token) {
      try {
        const { clearAuthBackoff, withSetSessionGuard } = await import("@/lib/supabase/client");
        clearAuthBackoff();
        const { data: sessionData, error: sessionError } = await withSetSessionGuard(() =>
          supabase.auth.setSession({
            access_token: data.session.access_token,
            refresh_token: data.session.refresh_token,
          })
        );

        if (sessionError) {
          console.warn("[signUp] Error setting session:", sessionError);
        } else if (sessionData.session) {
          console.log("[signUp] Session set successfully");
        }
      } catch (sessionErr) {
        console.warn("[signUp] Error setting session:", sessionErr);
        // Continue even if session setting fails - cookies might have it
      }
    } else {
      // If no session in response, check if it's in cookies (API route might have set it)
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        console.log("[signUp] Session found in cookies");
      }
    }

    return {
      success: true,
      message: data.message || "Account created! Redirecting you to onboarding...",
      user: {
        id: data.user.id,
        email: data.user.email || email,
      },
      session: data.session, // present when email confirmation is OFF (logged in immediately)
      preauthToken: data.preauthToken, // legacy SMS-OTP resume path
      resumed: data.resumed === true,
      needsEmailConfirmation: data.needsEmailConfirmation === true,
    };
  } catch (error) {
    console.error("Sign up error:", error);
    const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
    return {
      success: false,
      message: errorMessage.includes("Database error") 
        ? "Profile creation failed. Your account was created, but we couldn't set up your profile. Please contact support."
        : errorMessage || "An unexpected error occurred. Please try again.",
    };
  }
}

export async function signIn(email: string, password: string, turnstileToken?: string): Promise<AuthResponse> {
  const clientStartTime = Date.now();
  
  // CRITICAL: Log token details before sending to API
  // SECURITY: Never log the full Turnstile token to the browser console. It is short-lived
  // and single-use, but there is no reason to expose it verbatim, and doing so unconditionally
  // (not gated to development) meant every real sign-in attempt printed it in production too.
  const tokenLogDetails = turnstileToken ? {
    tokenLength: turnstileToken.length,
    tokenFirst6: turnstileToken.substring(0, 6),
    tokenLast6: turnstileToken.substring(turnstileToken.length - 6),
  } : null;
  
  console.log("[EMAIL LOGIN] [signIn] 📤 Client request started", {
    timestamp: new Date().toISOString(),
    hasToken: !!turnstileToken,
    tokenDetails: tokenLogDetails,
  });
  if (turnstileToken) {
    console.log("[EMAIL LOGIN] (a) Token provided to signIn (expected from fresh token at submit time)", {
      tokenLength: turnstileToken.length,
      timestamp: new Date().toISOString(),
    });
  }
  
  try {
    // Call signin API route (which handles Turnstile verification and Supabase signin)
    const fetchStartTime = Date.now();
    const emailToSend = email.toLowerCase().trim();
    const requestBody = { 
      email: emailToSend,
      password,
      turnstileToken,
    };
    
    const maskEmail = (e: string) => {
      if (!e || e.length < 3) return "***";
      const at = e.indexOf("@");
      if (at <= 0) return e.slice(0, 2) + "***";
      return e.slice(0, 2) + "***" + e.slice(at);
    };
    console.log("[EMAIL LOGIN] [INVESTIGATION] Client sending to API", {
      emailSent: maskEmail(emailToSend),
      emailLength: emailToSend.length,
      // SECURITY: character codes decode trivially back to the exact characters, which defeats
      // the maskEmail() redaction immediately above. Removed rather than "masked twice".
      hasPassword: !!password,
      passwordLength: password?.length ?? 0,
      hasCaptchaToken: !!turnstileToken,
      captchaTokenPassed: turnstileToken ? "YES" : "NO",
      timestamp: new Date().toISOString(),
    });
    console.log("[EMAIL LOGIN] [signIn] 📤 Sending request to API", {
      hasEmail: !!requestBody.email,
      hasPassword: !!requestBody.password,
      hasToken: !!requestBody.turnstileToken,
      tokenFirst6: requestBody.turnstileToken?.substring(0, 6) || "none",
      tokenLast6: requestBody.turnstileToken?.substring(requestBody.turnstileToken.length - 6) || "none",
      timestamp: new Date().toISOString(),
    });
    
    // CRITICAL: send the X-Platform header from either native shell. The server needs it to decide
    // whether to require a Turnstile token. On Android the User-Agent also carries "Capacitor" so the
    // header is a fallback; on iOS the User-Agent carries nothing, so this header is the ONLY signal
    // and omitting it locks iPhone users out of the app.
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    const signinPlatform = nativePlatformHeader();
    if (signinPlatform) {
      headers["X-Platform"] = signinPlatform;
    }

    const response = await fetch(getApiUrl("/api/auth/signin"), {
      method: "POST",
      headers,
      body: JSON.stringify(requestBody),
      credentials: "include", // CRITICAL: Required for Set-Cookie to be stored (fixes Android login loop)
    });

    const fetchTime = Date.now() - fetchStartTime;
    console.log("[signIn] 📥 Fetch complete", {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      fetchTime: `${fetchTime}ms`,
      timestamp: new Date().toISOString(),
    });

    // CRITICAL: Handle non-JSON responses (e.g., HTML error pages)
    let data: any;
    const contentType = response.headers.get("content-type");
    const isJson = contentType?.includes("application/json");
    
    try {
      if (isJson) {
        data = await response.json();
      } else {
        // Non-JSON response - likely an error page
        const text = await response.text();
        console.error("[signIn] ❌ Non-JSON response received", {
          status: response.status,
          contentType,
          textPreview: text.substring(0, 200),
          timestamp: new Date().toISOString(),
        });
        return {
          success: false,
          message: response.status === 500 
            ? "Server error. Please try again later." 
            : "Sign in failed. Please try again.",
        };
      }
    } catch (jsonError) {
      console.error("[signIn] ❌ Failed to parse response", {
        error: jsonError instanceof Error ? jsonError.message : String(jsonError),
        status: response.status,
        contentType,
        timestamp: new Date().toISOString(),
      });
      return {
        success: false,
        message: "Invalid response from server. Please try again.",
      };
    }
    
    const totalTime = Date.now() - clientStartTime;
    console.log("[signIn] ✅ Client request complete", {
      success: data?.success,
      hasError: !!data?.error,
      errorMessage: data?.error,
      totalTime: `${totalTime}ms`,
      timestamp: new Date().toISOString(),
    });

    // Right password on a device that has not passed the emailed code yet. Not an error, and
    // deliberately no session: the login page moves on to the code step.
    if (response.ok && data?.requiresStepUp && typeof data.challengeToken === "string") {
      return {
        success: false,
        requiresStepUp: true,
        stepUpReason: data.stepUpReason,
        challengeToken: data.challengeToken,
        emailHint: data.emailHint,
        codeSent: data.codeSent !== false,
        resendInSeconds: typeof data.resendInSeconds === "number" ? data.resendInSeconds : 0,
        message: data.message,
      };
    }

    if (!response.ok) {
      console.error("[EMAIL LOGIN] [INVESTIGATION] API returned error", {
        status: response.status,
        errorMessage: data?.error,
        errorCode: data?.errorCode,
        details: data?.details,
        timestamp: new Date().toISOString(),
      });
      return {
        success: false,
        message: data?.error || data?.message || "Invalid email or password. Please try again.",
        errorCode: data?.errorCode,
        details: data?.details,
      };
    }

    if (!data.user) {
      console.error("[signIn] No user returned from API:", {
        timestamp: new Date().toISOString(),
      });
      return {
        success: false,
        message: "Sign in failed. Please try again.",
      };
    }

    // CRITICAL: Session must be returned to caller - they will set it synchronously
    // The login page will handle setting the session to ensure it's done before redirect
    // This ensures reliable session creation and prevents race conditions

    return {
      success: true,
      message: data.message || "Signed in successfully.",
      user: {
        id: data.user.id,
        email: data.user.email || email,
      },
      session: data.session,
      requiresStepUp: data.requiresStepUp,
      requiresPhone: data.requiresPhone,
      preauthToken: data.preauthToken,
      stepUpReason: data.stepUpReason,
    };
  } catch (error) {
    console.error("[signIn] ❌ Sign in error:", {
      error,
      errorType: typeof error,
      errorMessage: error instanceof Error ? error.message : String(error),
      errorStack: error instanceof Error ? error.stack : undefined,
      errorStringified: JSON.stringify(error, Object.getOwnPropertyNames(error)),
      timestamp: new Date().toISOString(),
    });
    const errorMessage = error instanceof Error 
      ? error.message 
      : typeof error === "string" 
        ? error 
        : "An unexpected error occurred";
    return {
      success: false,
      message: errorMessage || "An unexpected error occurred. Please try again.",
    };
  }
}

export async function resendConfirmationEmail(email: string): Promise<AuthResponse> {
  try {
    // NO magic links - email confirmation uses OTP codes only
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      // Do NOT set emailRedirectTo - we use OTP codes, not magic links
    });

    if (error) {
      console.error("Resend confirmation error:", error);
      return {
        success: false,
        message: error.message || "Failed to resend confirmation email. Please try again.",
      };
    }

    return {
      success: true,
      message: "Confirmation email sent! Please check your inbox and click the confirmation link.",
    };
  } catch (error) {
    console.error("Resend confirmation error:", error);
    return {
      success: false,
      message: "An unexpected error occurred. Please try again.",
    };
  }
}

/**
 * Start an OAuth sign-in (Stage 3). Redirects the browser to the provider; on
 * return the user lands on /auth/callback which exchanges the code for a session.
 *
 * Requires the provider to be enabled in the Supabase dashboard (Auth → Providers)
 * with matching OAuth credentials and the /auth/callback redirect URL — until then
 * this surfaces a provider-not-enabled error.
 */
export async function signInWithProvider(
  provider: "google" | "linkedin_oidc"
): Promise<{ success: boolean; message?: string }> {
  try {
    const redirectTo =
      typeof window !== "undefined" ? `${window.location.origin}/auth/callback` : undefined;
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo },
    });
    if (error) {
      return { success: false, message: error.message };
    }
    // Success means the browser is now navigating to the provider.
    return { success: true };
  } catch (e) {
    return {
      success: false,
      message: e instanceof Error ? e.message : "Could not start sign-in. Please try again.",
    };
  }
}

export async function signOut(): Promise<void> {
  // Log signOut call with stack trace for debugging
  if (typeof window !== "undefined") {
    const { logSignOutCall } = await import("@/lib/debug/session-logger");
    logSignOutCall("auth.signOut", "Explicit signOut call");
  }
  await supabase.auth.signOut({ scope: "global" });
}

export async function getCurrentUser() {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    
    // CRITICAL: Only clear session on explicit invalid refresh token errors
    // Social apps (Instagram, Facebook, Hinge) never log users out on network errors
    // Preserve session even if there are temporary issues
    if (error) {
      const isInvalidTokenError = isDefinitiveInvalidRefreshTokenError(error);
      
      // Only clear on definitive invalid token errors, not network issues
      if (isInvalidTokenError) {
        console.warn("[getCurrentUser] Invalid refresh token detected, clearing session", {
          error: error.message,
        });
        if (typeof window !== "undefined") {
          const { logSignOutCall } = await import("@/lib/debug/session-logger");
          logSignOutCall("auth.getCurrentUser.invalidToken", "Invalid refresh token in getCurrentUser");
        }
        await supabase.auth.signOut();
        return null;
      } else {
        // Network errors, timeouts, etc. - preserve session like social apps do
        console.log("[getCurrentUser] Non-fatal error (preserving session):", error.message);
        // Try to get user from stored session instead
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          return session.user;
        }
        // If no session either, return null but don't clear anything
        return null;
      }
    }
    
    return user;
  } catch (error: any) {
    // CRITICAL: Only clear on explicit invalid refresh token errors
    // All other errors (network, timeout, etc.) should preserve the session
    const isInvalidTokenError = isDefinitiveInvalidRefreshTokenError(error);
    
    if (isInvalidTokenError) {
      console.warn("[getCurrentUser] Invalid refresh token detected (catch), clearing session", {
        error: error.message,
      });
      if (typeof window !== "undefined") {
        const { logSignOutCall } = await import("@/lib/debug/session-logger");
        logSignOutCall("auth.getCurrentUser.catch.invalidToken", "Invalid refresh token in getCurrentUser catch");
      }
      await supabase.auth.signOut();
      return null;
    }
    
    // For all other errors (network, timeout, etc.), try to get user from stored session
    console.log("[getCurrentUser] Error getting user (non-fatal, preserving session):", error?.message || String(error));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        return session.user;
      }
    } catch (sessionError) {
      // Even if getting session fails, don't clear anything - might be temporary
      console.log("[getCurrentUser] Could not get session (preserving auth state):", sessionError);
    }
    
    return null;
  }
}

export async function getSession() {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    // CRITICAL: Social apps (Instagram, Facebook, Hinge) never log users out on network errors
    // Only clear session on explicit invalid refresh token errors
    // Preserve session even if there are temporary network issues
    if (error) {
      const isInvalidTokenError = isDefinitiveInvalidRefreshTokenError(error);
      
      if (isInvalidTokenError) {
        console.warn("[getSession] Invalid refresh token detected, clearing session", {
          error: error.message,
        });
        if (typeof window !== "undefined") {
          const { logSignOutCall } = await import("@/lib/debug/session-logger");
          logSignOutCall("auth.getSession.invalidToken", "Invalid refresh token in getSession");
        }
        await supabase.auth.signOut();
        return null;
      }
      
      console.log("[getSession] Session check error (non-fatal, preserving session):", error.message);
      return null;
    }
    
    return session;
  } catch (error: any) {
    // CRITICAL: Only clear on explicit invalid refresh token errors
    // All other errors should preserve the session
    const isInvalidTokenError = isDefinitiveInvalidRefreshTokenError(error);
    
    if (isInvalidTokenError) {
      console.warn("[getSession] Invalid refresh token detected (exception), clearing session", {
        error: error.message,
      });
      if (typeof window !== "undefined") {
        const { logSignOutCall } = await import("@/lib/debug/session-logger");
        logSignOutCall("auth.getSession.catch.invalidToken", "Invalid refresh token in getSession catch");
      }
      await supabase.auth.signOut();
      return null;
    }
    
    console.log("[getSession] Exception getting session (non-fatal, preserving session):", error?.message || String(error));
    return null;
  }
}

// Passwordless email-code sign-in (sendOTP / verifyOTP) was removed on 2026-10-08: nothing called
// it, and it signed members in without a password. Sign-in is signIn() above, with an emailed
// code on new devices (components/auth/email-code-step.tsx).


